import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { IoIosEye } from "react-icons/io";
import { IoIosEyeOff } from "react-icons/io";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { changePasswordSchema } from "@/utils/schemaHelper";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";
import { useNavigate, useParams } from "react-router-dom";
import {
  logoutUser,
  resetPasswordApi,
  verifyChangePasswordApi,
} from "@/modules/authenticationService";
import { ToastSuccessMessages } from "@/enums/errorMessages";
import { ROUTES } from "@/enums/routes";
import { TiTick } from "react-icons/ti";

import { useDispatch, useSelector } from "react-redux";

import { Loader2 } from "lucide-react";
import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import { logout } from "@/store/redux/authSlice";
import { Profile } from "@/types/authentication";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { ReusableButton } from "@/reusable/reusableButton";

const ResetPassword = () => {
  const { uid, token } = useParams();

  if (!uid || !token) throw "Sorry this is not available for the moment";
  const dispatch = useDispatch();
  const user = useSelector(
    (state: RootState) => state.auth.profile,
  ) as Profile | null;
  const loading = useSelector((state: RootState) => state.app.loading);
  const [tokenVerified, setTokenVerified] = useState<boolean>(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const form = useForm<z.infer<typeof changePasswordSchema>>({
    resolver: zodResolver(changePasswordSchema),
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    if (user) {
      logoutUser(user.refreshToken);
      dispatch(logout());
    }
  }, [user, dispatch]);
  useEffect(() => {
    const verifyToken = async (token: string) => {
      try {
        await verifyChangePasswordApi(uid, token);
        setTokenVerified(true);
      } catch (error) {
        const actualError = ErrorHelper(error);
        const message = Array.isArray(actualError)
          ? actualError[0]
          : actualError;
        toastMessage("error", message);
        navigate(ROUTES.LOGIN);
      }
    };

    verifyToken(token);
  }, [token]);

  const onSubmit = async (values: z.infer<typeof changePasswordSchema>) => {
    if (loading) return;

    dispatch(setLoading(true));

    try {
      await resetPasswordApi(values, token, uid);

      navigate(ROUTES.LOGIN);

      toastMessage("success", ToastSuccessMessages.PASSWORD_CHANGED);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };
  if (tokenVerified === false) {
    // You can show a loading spinner here
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }
  return (
    <div className="flex min-h-screen items-center justify-center w-screen bg-gray-100 px-4 ">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-center text-2xl font-semibold mb-2 text-gray-800">
          Reset Password
        </h2>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 ">
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>New Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.newPassword
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.newPassword && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                    <div
                      className="absolute right-7 top-2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                    {}
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel> Confirm New Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.confirmPassword
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value &&
                        !form.formState.errors.confirmPassword && (
                          <TiTick className="text-green-700" />
                        )}
                    </div>
                    <div
                      className="absolute right-7 top-2"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                    {}
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            <div className="flex justify-center ">
              <ReusableButton
                className="w-full"
                label="Reset"
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
export default ResetPassword;
